import { CommentsManager } from './comments.js';

/**
 * Reactive signal system for managing state changes
 */
class ReactiveSignal {
    constructor(initialValue = null) {
        this.value = initialValue;
        this.subscribers = new Set();
    }

    get() {
        return this.value;
    }

    set(newValue) {
        const oldValue = this.value;
        this.value = newValue;
        this.notify(newValue, oldValue);
    }

    subscribe(callback) {
        this.subscribers.add(callback);
        // Return unsubscribe function
        return () => this.subscribers.delete(callback);
    }

    notify(newValue, oldValue) {
        this.subscribers.forEach(callback => {
            try {
                callback(newValue, oldValue);
            } catch (error) {
                console.error('Error in signal subscriber:', error);
            }
        });
    }
}

/**
 * CAD State capture and restoration utilities
 */
export class CADState {
    constructor(camera, projectionMode, navigationMode, selectedMesh = null, visibleMeshes = null) {
        this.camera = camera ? {
            eye: { x: camera.eye.x, y: camera.eye.y, z: camera.eye.z },
            center: { x: camera.center.x, y: camera.center.y, z: camera.center.z },
            up: { x: camera.up.x, y: camera.up.y, z: camera.up.z },
            fov: camera.fov
        } : null;
        this.projectionMode = projectionMode;
        this.navigationMode = navigationMode;
        this.selectedMesh = selectedMesh;
        this.visibleMeshes = visibleMeshes ? [...visibleMeshes] : null;
        this.timestamp = Date.now();
    }

    static fromViewer(viewer, navigator = null, selectedMeshId = null) {
        const navigationCamera = viewer.navigation?.GetCamera();
        const visibleMeshes = navigator ? navigator.GetVisibleMeshes?.() : null;

        return new CADState(
            navigationCamera,
            viewer.projectionMode,
            viewer.navigation?.navigationMode,
            selectedMeshId,
            visibleMeshes
        );
    }

    isEqual(other) {
        if (!other) return false;

        const camerasEqual = this.camera && other.camera &&
            Math.abs(this.camera.eye.x - other.camera.eye.x) < 0.001 &&
            Math.abs(this.camera.eye.y - other.camera.eye.y) < 0.001 &&
            Math.abs(this.camera.eye.z - other.camera.eye.z) < 0.001 &&
            Math.abs(this.camera.center.x - other.camera.center.x) < 0.001 &&
            Math.abs(this.camera.center.y - other.camera.center.y) < 0.001 &&
            Math.abs(this.camera.center.z - other.camera.center.z) < 0.001;

        return camerasEqual &&
               this.projectionMode === other.projectionMode &&
               this.navigationMode === other.navigationMode &&
               this.selectedMesh === other.selectedMesh;
    }
}

/**
 * Global Comments Manager with reactive signals and CAD state integration
 */
export class GlobalCommentsManager {
    constructor(apiConfig = null) {
        // Base comments manager
        this.commentsManager = new CommentsManager(apiConfig);

        // Reactive signals
        this.signals = {
            comments: new ReactiveSignal(new Map()),
            activeComment: new ReactiveSignal(null),
            cadState: new ReactiveSignal(null),
            isCommentModeActive: new ReactiveSignal(false),
            pendingComment: new ReactiveSignal(null)
        };

        // References to viewer components
        this.viewer = null;
        this.navigator = null;
        this.website = null;

        // Comment composition state
        this.isComposingComment = false;
        this.commentPosition = null;
        this.commentCADState = null;

        // CAD state stack for viewing comments
        this.cadStateStack = [];
        this.currentViewingComment = null;
        this.highlightedMeshId = null; // Track currently highlighted mesh for comments

        // Bind methods
        this.captureCADState = this.captureCADState.bind(this);
        this.restoreCADState = this.restoreCADState.bind(this);
        this.onCommentAdded = this.onCommentAdded.bind(this);
        this.onCommentUpdated = this.onCommentUpdated.bind(this);
        this.onCommentDeleted = this.onCommentDeleted.bind(this);

        // Setup event listeners for base comments manager
        this.setupEventListeners();
    }

    /**
     * Initialize with viewer components
     */
    initialize(viewer, navigator, website) {
        this.viewer = viewer;
        this.navigator = navigator;
        this.website = website;

        // Initial state sync
        this.syncCommentsFromBase();
        this.updateCADState();
    }

    /**
     * Setup event listeners for the base comments manager
     */
    setupEventListeners() {
        this.commentsManager.addEventListener('commentAdded', this.onCommentAdded);
        this.commentsManager.addEventListener('commentUpdated', this.onCommentUpdated);
        this.commentsManager.addEventListener('commentDeleted', this.onCommentDeleted);
        this.commentsManager.addEventListener('commentsLoaded', () => {
            this.syncCommentsFromBase();
        });
    }

    /**
     * Event handlers for base comments manager
     */
    onCommentAdded(event) {
        console.log("<<<< onCommentAdded", event)
        this.syncCommentsFromBase();
        this.signals.activeComment.set(event.detail?.comment ?? null);
    }

    onCommentUpdated(event) {
        this.syncCommentsFromBase();
    }

    onCommentDeleted(event) {
        this.syncCommentsFromBase();
        if (this.signals.activeComment.get()?.id === event.detail?.commentId) {
            this.signals.activeComment.set(null);
        }
    }

    /**
     * Sync comments from base manager to reactive signals
     */
    syncCommentsFromBase() {
        const commentsMap = new Map();
        for (const [id, comment] of this.commentsManager.comments) {
            commentsMap.set(id, comment);
        }
        this.signals.comments.set(commentsMap);
    }

    /**
     * Capture current CAD state
     */
    captureCADState(selectedMeshId = null) {
        if (!this.viewer) return null;

        const cadState = CADState.fromViewer(this.viewer, this.navigator, selectedMeshId);
        this.signals.cadState.set(cadState);
        return cadState;
    }

    /**
     * Restore CAD state from a comment
     */
    async restoreCADState(cadState, animated = true) {
        if (!cadState || !this.viewer || !cadState.camera) return;

        try {
            // Import camera class for restoration
            const { Camera } = await import('../engine/viewer/camera.js');
            const { Coord3D } = await import('../engine/geometry/coord3d.js');

            // Reconstruct camera object
            const camera = new Camera(
                new Coord3D(cadState.camera.eye.x, cadState.camera.eye.y, cadState.camera.eye.z),
                new Coord3D(cadState.camera.center.x, cadState.camera.center.y, cadState.camera.center.z),
                new Coord3D(cadState.camera.up.x, cadState.camera.up.y, cadState.camera.up.z),
                cadState.camera.fov
            );

            // Set projection mode if different
            if (cadState.projectionMode !== this.viewer.projectionMode) {
                this.viewer.SetProjectionMode(cadState.projectionMode);
            }

            // Set navigation mode if different
            if (cadState.navigationMode !== this.viewer.navigation?.navigationMode) {
                this.viewer.navigation?.SetNavigationMode(cadState.navigationMode);
            }

            // Move camera
            const animationSteps = animated ? 40 : 0;
            this.viewer.navigation?.MoveCamera(camera, animationSteps);

            // Restore mesh visibility if available
            if (cadState.visibleMeshes && this.navigator) {
                // Hide all meshes first
                this.navigator.ShowAllMeshes(false);
                // Show only the meshes that were visible
                cadState.visibleMeshes.forEach(meshId => {
                    this.navigator.ShowMesh(meshId, true);
                });
            }

            // Select mesh if specified
            if (cadState.selectedMesh && this.navigator) {
                this.navigator.SetSelection(cadState.selectedMesh);
            }

            this.updateCADState();
        } catch (error) {
            console.error('Failed to restore CAD state:', error);
        }
    }

    /**
     * Update current CAD state signal
     */
    updateCADState() {
        this.captureCADState();
    }

    /**
     * Start comment composition at specific position with CAD state
     */
    startCommentComposition(position, meshUserData = null) {
        this.isComposingComment = true;
        this.commentPosition = position;
        this.commentCADState = this.captureCADState(meshUserData?.originalMeshInstance?.id);

        this.signals.isCommentModeActive.set(true);
        this.signals.pendingComment.set({
            position,
            cadState: this.commentCADState,
            meshUserData
        });
    }

    /**
     * Cancel comment composition
     */
    cancelCommentComposition() {
        this.isComposingComment = false;
        this.commentPosition = null;
        this.commentCADState = null;

        this.signals.isCommentModeActive.set(false);
        this.signals.pendingComment.set(null);
    }

    /**
     * Submit comment with captured CAD state
     */
    async submitComment(message, parentId = null) {
        if (!this.isComposingComment || !message.trim()) {
            throw new Error('No active comment composition or empty message');
        }

        try {
            const currentUser = this.commentsManager.getCurrentUser();
            if (!currentUser) {
                throw new Error('User not authenticated');
            }

            const commentData = {
                userId: currentUser.id,
                message: message.trim(),
                parentId,
                // Store coordinates and CAD state in new Comment fields
                coordinates: this.commentPosition ? [this.commentPosition.x, this.commentPosition.y] : null,
                attached_to: null, // TODO: Set to file ID when available
                data: {
                    // CAD state information
                    camera: this.commentCADState?.camera || null,
                    projectionMode: this.commentCADState?.projectionMode || null,
                    navigationMode: this.commentCADState?.navigationMode || null,
                    selectedMesh: this.commentCADState?.selectedMesh || null,
                    visibleMeshes: this.commentCADState?.visibleMeshes || null,
                    // Legacy cadReference data moved here
                    cadReference: {
                        type: 'cad_state',
                        cadState: this.commentCADState,
                        position: this.commentPosition,
                        timestamp: Date.now()
                    },
                    timestamp: Date.now()
                }
            };

            const comment = await this.commentsManager.addComment(commentData);

            // Reset composition state
            this.cancelCommentComposition();

            return comment;
        } catch (error) {
            console.error('Failed to submit comment:', error);
            throw error;
        }
    }

    /**
     * Navigate to a comment (restore its CAD state)
     */
    async navigateToComment(commentId, animated = true) {
        const comment = this.commentsManager.comments.get(commentId);
        if (!comment) {
            throw new Error('Comment not found');
        }

        // Save current state to stack if not already viewing a comment
        if (!this.currentViewingComment) {
            const currentState = this.captureCADState();
            if (currentState) {
                this.cadStateStack.push(currentState);
                console.log('Saved current CAD state to stack');
            }
        }

        // Set as active comment
        this.signals.activeComment.set(comment);
        this.currentViewingComment = comment;

                // Restore CAD state from comment data
        let cadStateToRestore = null;

        // Try new data field first (preferred)
        if (comment.data?.camera) {
            cadStateToRestore = {
                camera: comment.data.camera,
                projectionMode: comment.data.projectionMode,
                navigationMode: comment.data.navigationMode,
                selectedMesh: comment.data.selectedMesh,
                visibleMeshes: comment.data.visibleMeshes
            };
        }
        // Try cadReference data inside comment.data (new structure)
        else if (comment.data?.cadReference?.cadState) {
            cadStateToRestore = comment.data.cadReference.cadState;
        }
        // Fallback to legacy cadReference (backward compatibility)
        else if (comment.cadReference?.cadState) {
            cadStateToRestore = comment.cadReference.cadState;
        }

        if (cadStateToRestore) {
            await this.restoreCADState(cadStateToRestore, animated);
        }

        // Highlight the mesh associated with this comment
        this.highlightCommentMesh(comment);

        return comment;
    }

    /**
     * Stop viewing current comment and restore previous CAD state
     */
    async stopViewingComment(animated = true) {
        if (!this.currentViewingComment) {
            return; // Not currently viewing a comment
        }

        console.log('Stopping comment viewing, restoring previous state');

        // Clear active comment
        this.signals.activeComment.set(null);
        this.currentViewingComment = null;

        // Clear comment mesh highlighting
        this.clearCommentMeshHighlight();

        // Restore previous state from stack
        if (this.cadStateStack.length > 0) {
            const previousState = this.cadStateStack.pop();
            await this.restoreCADState(previousState, animated);
            console.log('Restored previous CAD state from stack');
        }
    }

    /**
     * Check if currently viewing a comment
     */
    isViewingComment() {
        return this.currentViewingComment !== null;
    }

    /**
     * Get currently viewing comment
     */
    getCurrentViewingComment() {
        return this.currentViewingComment;
    }

    /**
     * Get comments with CAD state
     */
    getCommentsWithCADState() {
        const comments = [];
        for (const [id, comment] of this.signals.comments.get()) {
            if (comment.data?.camera || comment.data?.cadReference?.cadState || comment.cadReference?.cadState) {
                comments.push(comment);
            }
        }
        return comments;
    }

    /**
     * Extract mesh ID from comment data
     */
    extractMeshIdFromComment(comment) {
        if (!comment) return null;

        // Try different sources for mesh ID
        // 1. New data structure: comment.data.selectedMesh
        if (comment.data?.selectedMesh) {
            return comment.data.selectedMesh;
        }

        // 2. CAD reference in data: comment.data.cadReference.cadState.selectedMesh
        if (comment.data?.cadReference?.cadState?.selectedMesh) {
            return comment.data.cadReference.cadState.selectedMesh;
        }

        // 3. Legacy cadReference: comment.cadReference.cadState.selectedMesh
        if (comment.cadReference?.cadState?.selectedMesh) {
            return comment.cadReference.cadState.selectedMesh;
        }

        return null;
    }

    /**
     * Highlight mesh associated with a comment
     */
    highlightCommentMesh(meshIdOrComment, highlightColor = null) {
        if (!this.viewer || !this.website) return;

        // Use a distinct color for comment highlighting (orange/amber)
        const commentHighlightColor = highlightColor || { r: 255, g: 165, b: 0 }; // Orange

        // Clear any existing comment highlighting
        this.clearCommentMeshHighlight();

        // Extract mesh ID if a comment object was passed
        let meshId = meshIdOrComment;
        if (typeof meshIdOrComment === 'object' && meshIdOrComment.id) {
            meshId = this.extractMeshIdFromComment(meshIdOrComment);
        }

        if (meshId) {
            this.highlightedMeshId = meshId;

            // Use the existing highlighting system
            this.viewer.SetMeshesHighlight(commentHighlightColor, (meshUserData) => {
                return meshUserData.originalMeshInstance.id.IsEqual(meshId);
            });
        }
    }

    /**
     * Clear comment mesh highlighting
     */
    clearCommentMeshHighlight() {
        if (!this.viewer || !this.highlightedMeshId) return;

        // Clear the highlight by setting an empty highlight function
        this.viewer.SetMeshesHighlight({ r: 0, g: 0, b: 0 }, () => false);
        this.highlightedMeshId = null;

        // Restore normal mesh selection highlighting if there's an active selection
        if (this.website && this.website.UpdateMeshesSelection) {
            this.website.UpdateMeshesSelection();
        }
    }

    /**
     * Subscribe to reactive signals
     */
    subscribe(signalName, callback) {
        if (this.signals[signalName]) {
            return this.signals[signalName].subscribe(callback);
        }
        throw new Error(`Unknown signal: ${signalName}`);
    }

    /**
     * Get signal value
     */
    getSignal(signalName) {
        if (this.signals[signalName]) {
            return this.signals[signalName].get();
        }
        throw new Error(`Unknown signal: ${signalName}`);
    }

    /**
     * Proxy methods to base comments manager
     */
    getCurrentUser() {
        return this.commentsManager.getCurrentUser();
    }

    async login(credentials) {
        return await this.commentsManager.login(credentials);
    }

    async logout() {
        return await this.commentsManager.logout();
    }

    getComments() {
        return this.commentsManager.getComments();
    }

    getComment(id) {
        return this.commentsManager.getComment(id);
    }

    async deleteComment(id) {
        return await this.commentsManager.deleteComment(id);
    }

    async editComment(id, newMessage) {
        return await this.commentsManager.editComment(id, newMessage);
    }

    exportComments() {
        return this.commentsManager.exportComments();
    }

    importComments(data) {
        this.commentsManager.importComments(data);
        this.syncCommentsFromBase();
    }

    /**
     * Cleanup
     */
    destroy() {
        // Remove event listeners
        this.commentsManager.removeEventListener('commentAdded', this.onCommentAdded);
        this.commentsManager.removeEventListener('commentUpdated', this.onCommentUpdated);
        this.commentsManager.removeEventListener('commentDeleted', this.onCommentDeleted);

        // Clear all signal subscribers
        Object.values(this.signals).forEach(signal => {
            signal.subscribers.clear();
        });

        // Clear highlighting
        this.clearCommentMeshHighlight();

        // Reset state
        this.cancelCommentComposition();
    }
}
